/**
 * Chat Configuration
 * Contains backend URLs, constants, and configuration values for the chat system
 */

import { ThinkingMode, ConversationType } from './chatTypes';

// Backend URL validation and construction
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

if (!BACKEND_BASE_URL) {
  throw new Error('REACT_APP_BACKEND_BASE_URL environment variable is required but not set');
}

/**
 * Backend API Endpoints for Thinking Modes
 */
export const CHAT_API_ENDPOINTS = {
  REFLECTION: `${BACKEND_BASE_URL}/api/v0/hegel/process-doppledenk-complete`,
  FAST_THINKING: `${BACKEND_BASE_URL}/api/v0/fast-thinking/process`,
  FRAKTAL: `${BACKEND_BASE_URL}/api/v1/hegel/process-fraktal`,
  PROMPT_GRAPH: `${BACKEND_BASE_URL}/api/v0/prompt-graph/execute`,
} as const;

/**
 * Chat Configuration Constants
 */
export const CHAT_CONFIG = {
  // Progress tracking steps for reflection mode
  REFLECTION_PROGRESS_STEPS: [
    'Completed first input processing',
    'Completed second input processing',
    'Completed parallel reflection processing',
    'Completed parallel existenz processing',
    'Completed parallel erscheinung processing',
    'Completed parallel output processing',
    'Completed final processing',
  ],
  
  // Default values
  DEFAULT_CONVERSATION_TYPE: 'reflection' as const,
  PLATFORM_NAME: 'chat-webapp',
  
  // Conversation types
  CONVERSATION_TYPES: {
    REFLECTION: 'reflection' as const,
    FRAKTAL: 'fraktal' as const,
  },
  
  // Default request parameters
  DEFAULT_REQUEST_PARAMS: {
    store_intermediate_results: true,
    platform: 'chat-webapp',
  },
  
  // Sein-II configuration (kept for reference)
  SEIN_II_CONFIG: {
    CHAIN_VERSION: 'SEIN_2',
    COMPLEXITY_LEVEL: 'sein_complexity_2',
  },
} as const;

/**
 * Configuration interface for thinking modes
 * Defines endpoint, conversation type, and request parameters for each mode
 */
export interface ModeConfig {
  /** Backend API endpoint for this mode */
  endpoint: string;
  /** Conversation type this mode belongs to */
  conversationType: ConversationType;
  /** Additional request parameters specific to this mode */
  requestParams?: Record<string, any>;
}

/**
 * Centralized configuration for all thinking modes
 * Adding a new mode: just add an entry here and update the ThinkingMode type
 */
export const MODE_CONFIG: Record<ThinkingMode, ModeConfig> = {
  'reflection': {
    endpoint: CHAT_API_ENDPOINTS.REFLECTION,
    conversationType: 'reflection',
    requestParams: {
      motivation_type_for_input_1: 'Dynamic Visionary',
      motivation_type_for_input_2: 'Order-loving protector',
      motivation_type_for_output: 'Understanding Mediator',
    }
  },
  'fast-thinking': {
    endpoint: CHAT_API_ENDPOINTS.FAST_THINKING,
    conversationType: 'reflection', // Fast thinking is part of reflection conversations
     requestParams: {
       web_search_enabled: true,
     }
  },
  'fraktal': {
    endpoint: CHAT_API_ENDPOINTS.FRAKTAL,
    conversationType: 'fraktal',
    requestParams: {
      base_model: 'gpt-4o',
      output_type: 'NONE',
      sein_chain_version: 'SEIN_2',
      extra_parameters: {
        fraktal_complexity: 'sein_complexity_2'
      }
    }
  },
  'prompt-graph': {
    endpoint: CHAT_API_ENDPOINTS.PROMPT_GRAPH,
    conversationType: 'prompt-graph',
    requestParams: {
      project_id: 'hegel_complete_outputs_920ae661',
    }
  }
};

/**
 * Mode availability rules - restricted to prompt-graph and fast-thinking only
 */
const AVAILABILITY_RULES = {
  firstMessage: ['prompt-graph'] as ThinkingMode[], // Only prompt-graph for new conversations
  // reflection: [] as ThinkingMode[], // Empty - legacy conversations are read-only
  // fraktal: [] as ThinkingMode[], // Empty - legacy conversations are read-only
  promptgraph: ['fast-thinking'] as ThinkingMode[], // Only fast-thinking for prompt-graph conversations
  default: ['prompt-graph'] as ThinkingMode[]
} as const;

/**
 * Get available thinking modes based on conversation context
 * @param conversationType - Type of the current conversation ('reflection' | 'fraktal' | 'prompt-graph')
 * @param isFirstMessage - Whether this is the first message in a conversation
 * @returns Array of available ThinkingMode values
 */
export const getAvailableModes = (
  conversationType?: ConversationType,
  isFirstMessage = false
): ThinkingMode[] => {
  // For new conversations (first message), show all modes
  if (isFirstMessage) {
    return AVAILABILITY_RULES.firstMessage;
  }
  
  // Use conversation type to determine available modes
  if (conversationType === 'fraktal') {
    return []; // Legacy conversations are read-only
  }
  
  if (conversationType === 'reflection') {
    return []; // Legacy conversations are read-only
  }
  
  if (conversationType === 'prompt-graph') {
    return AVAILABILITY_RULES.promptgraph;
  }
  
  // Default fallback
  return AVAILABILITY_RULES.default;
};

/**
 * Default mode rules - more declarative approach
 */
const DEFAULT_MODE_RULES = {
  firstMessage: 'prompt-graph' as ThinkingMode, // Default to prompt-graph for new conversations
  //reflection: 'reflection' as ThinkingMode, // Only reflection mode available
  //fraktal: 'fraktal' as ThinkingMode,
  promptgraph: 'fast-thinking' as ThinkingMode, // Only fast-thinking available
  default: 'prompt-graph' as ThinkingMode
} as const;

/**
 * Get the default thinking mode for a conversation context
 * @param conversationType - Type of the current conversation
 * @param isFirstMessage - Whether this is the first message
 * @returns Default ThinkingMode for the context
 */
export const getDefaultMode = (
  conversationType?: ConversationType,
  isFirstMessage = false
): ThinkingMode => {
  // For new conversations, default to reflection
  if (isFirstMessage) {
    return DEFAULT_MODE_RULES.firstMessage;
  }
  
  // Use conversation type to determine default mode
  // if (conversationType === 'fraktal') {
  //   return DEFAULT_MODE_RULES.fraktal;
  // }
  
  // if (conversationType === 'reflection') {
  //   return DEFAULT_MODE_RULES.reflection;
  // }
  
  if (conversationType === 'prompt-graph') {
    return DEFAULT_MODE_RULES.promptgraph;
  }
  
  // Default fallback
  return DEFAULT_MODE_RULES.default;
};

/**
 * Get request parameters for a specific thinking mode
 * Centralized helper to access mode-specific request parameters
 * @param mode - The thinking mode
 * @returns Request parameters object for the mode
 */
export const getRequestParams = (mode: ThinkingMode): Record<string, any> => {
  return MODE_CONFIG[mode].requestParams || {};
};

/**
 * Check if a conversation type is legacy (read-only)
 * @param conversationType - The conversation type to check
 * @returns true if the conversation type is legacy (reflection or fraktal)
 */
export const isLegacyConversationType = (conversationType?: ConversationType): boolean => {
  return conversationType === 'reflection' || conversationType === 'fraktal';
};